// Import React utilities (React.forwardRef lets us pass refs down)
import * as React from "react"

// Utility to merge Tailwind classes conditionally
import { cn } from "@/lib/utils"

// 🟢 Updated Card: now supports `variant` prop for scalable theming ("dark" is default, "light" is alternative)
const Card = React.forwardRef(
  ({ className, variant = "dark", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Default variant styles
        "rounded-xl shadow-md",
        variant === "dark" && "bg-transparent text-swan",
        variant === "light" && "bg-white text-black",
        className
      )}
      {...props}
    />
  )
)
Card.displayName = "Card"

// 🟢 Updated CardHeader:
// Adjusted padding from p-6 to custom paddings (px-4, pt-4, pb-2) to eliminate top whitespace
// that previously created visual "fake borders" around your thumbnail
const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col overflow-hidden space-y-1.5 px-4 pt-4 pb-2", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

// 🟡 CardTitle (unchanged)
// Styles title with default font and tracking for consistency across cards
const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

// 🟡 CardDescription (unchanged)
// Styles description text with muted, smaller typography
const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

// 🟢 Updated CardContent:
// Changed padding to match the new header/footer pattern (p-4)
// to maintain internal spacing consistency and minimize visual discrepancy
const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("px-4 pb-4 pt-0", className)}
    {...props}
  />
))
CardContent.displayName = "CardContent"

// 🟢 Updated CardFooter:
// Added justify-between (for flexible alignment), padding adjustments (px-4, pb-4, pt-2)
// mt-auto ensures footer is always pushed to the card bottom, preserving vertical structure
const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center justify-between px-4 pb-4 pt-2 mt-auto", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

// Export all card-related components for reuse across the app
export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
